import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Newspaper, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useBlogs, useUpdateBlog, useDeleteBlog } from '@/hooks/data/useBlog'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'

const PAGE_SIZE = 20

function BlogPage() {
  const { data: posts = [], isLoading, isError, error, isFetching, refetch } = useBlogs()
  const update = useUpdateBlog()
  const del = useDeleteBlog()
  const confirmDelete = useDeleteConfirm()

  const [q, setQ] = useState('')
  const [category, setCategory] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const categories = useMemo(() => Array.from(new Set(posts.map((p) => p.category).filter(Boolean))).sort(), [posts])

  const filtered = posts.filter(
    (p) => (!q || p.title.toLowerCase().includes(q.toLowerCase())) && (!category || p.category === category),
  )
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const bulkSetPublished = async (isPublished: boolean) => {
    const targets = posts.filter((p) => selected.has(p.id))
    await Promise.all(
      targets.map((p) =>
        update.mutateAsync({
          id: p.id,
          body: {
            title: p.title,
            slug: p.slug,
            excerpt: p.excerpt,
            content: p.content,
            category: p.category,
            publishedOn: p.publishedOn,
            readTimeMinutes: p.readTimeMinutes,
            imageUrl: p.imageUrl,
            isPublished,
          },
        }),
      ),
    )
    setSelected(new Set())
  }

  const bulkDelete = () => {
    confirmDelete(`${selected.size} post${selected.size === 1 ? '' : 's'}`, async () => {
      await Promise.all(Array.from(selected).map((id) => del.mutateAsync(id)))
      setSelected(new Set())
    })
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Newspaper className="w-6 h-6" /> Blog
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Manage marketing blog posts (BlogController).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <Link to="/blog/new" className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Post
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setPage(1)
          }}
          placeholder="Search posts…"
          className="w-full md:w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        />
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value)
            setPage(1)
          }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-sm">
          <span className="text-blue-700 dark:text-blue-300">{selected.size} selected</span>
          <button onClick={() => bulkSetPublished(true)} className="px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            Publish
          </button>
          <button onClick={() => bulkSetPublished(false)} className="px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            Unpublish
          </button>
          <button onClick={bulkDelete} className="px-2 py-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-red-600">
            Delete
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading posts…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load posts: {(error as Error)?.message}.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-gray-500">No posts found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2 w-8" />
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelected(p.id)} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 dark:text-gray-100">{p.title}</div>
                    <div className="text-xs text-gray-500">{p.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{p.category || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(p.publishedOn).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <InlineToggle
                      checked={p.isPublished}
                      label={p.isPublished ? 'Published' : 'Draft'}
                      onToggle={(next) =>
                        update.mutateAsync({
                          id: p.id,
                          body: {
                            title: p.title,
                            slug: p.slug,
                            excerpt: p.excerpt,
                            content: p.content,
                            category: p.category,
                            publishedOn: p.publishedOn,
                            readTimeMinutes: p.readTimeMinutes,
                            imageUrl: p.imageUrl,
                            isPublished: next,
                          },
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Link to="/blog/$id" params={{ id: p.id }} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit post">
                        <Pencil className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() =>
                          confirmDelete(p.title, async () => {
                            await del.mutateAsync(p.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete post"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-2 py-1 rounded disabled:opacity-40">
            ← Prev
          </button>
          <span className="text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-2 py-1 rounded disabled:opacity-40">
            Next →
          </button>
        </div>
      )}
    </div>
  )
}

export const Route = createFileRoute('/(admin)/blog/')({
  component: BlogPage,
})
